import { useState, useRef, useCallback } from 'react'
import { Factory, FileText, Leaf, Warning, CheckCircle, Drop, ArrowCounterClockwise, X } from '@phosphor-icons/react'

export default function MaterialsAnalysis({ project }) {
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [analyzing, setAnalyzing] = useState(false)
  const [analysisResult, setAnalysisResult] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)
  const dragCounter = useRef(0)

  // Drag & Drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    dragCounter.current = 0
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) handleFilesAdded(files)
  }, [])

  const handleInput = (e) => {
    const files = Array.from(e.target.files)
    if (files.length > 0) handleFilesAdded(files)
    e.target.value = ''
  }

  const handleFilesAdded = (files) => {
    const newFiles = files.map(f => ({ id: Date.now() + Math.random(), name: f.name, size: f.size, file: f }))
    setUploadedFiles(prev => [...prev, ...newFiles])
  }

  const handleRemoveFile = (id) => setUploadedFiles(prev => prev.filter(f => f.id !== id))

  // Analizar materiales con IA
  const analyzeMaterials = async () => {
    if (!uploadedFiles.length) return
    setAnalyzing(true)
    setAnalysisResult(null)

    try {
      // 1. Convertir archivos a base64
      const filesData = await Promise.all(
        uploadedFiles.slice(0, 5).map(f => new Promise((resolve) => {
          const reader = new FileReader()
          reader.onload = () => {
            const base64 = reader.result.split(',')[1]
            const isPDF = f.name.toLowerCase().endsWith('.pdf')
            const isImage = f.name.match(/\.(jpg|jpeg|png)$/i)
            resolve({
              name: f.name,
              base64,
              mediaType: isPDF ? 'application/pdf' : isImage ? 'image/jpeg' : null
            })
          }
          reader.readAsDataURL(f.file)
        }))
      )

      // 2. Construir prompt para Claude
      const userContent = []

      filesData.forEach(f => {
        if (f.mediaType === 'application/pdf') {
          userContent.push({ type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: f.base64 } })
        } else if (f.mediaType?.startsWith('image/')) {
          userContent.push({ type: 'image', source: { type: 'base64', media_type: f.mediaType, data: f.base64 } })
        }
      })

      userContent.push({
        type: 'text',
        text: `Eres un experto en materiales de construcción sostenibles y análisis de impacto ambiental.

Analiza las fichas técnicas de materiales adjuntas y proporciona un informe detallado sobre:

1. **Saludabilidad**: ¿Los materiales son seguros para la salud humana? ¿Contienen sustancias tóxicas, COV, formaldehídos?
2. **Sostenibilidad**: ¿Son reciclables, biodegradables, de origen renovable? ¿Tienen certificaciones (FSC, PEFC, Cradle to Cradle)?
3. **Huella de Carbono**: Emisiones de CO2 en fabricación y transporte. ¿Hay datos de carbono embebido?
4. **Durabilidad**: Vida útil esperada, necesidad de mantenimiento
5. **Circularidad**: ¿Contienen material reciclado? ¿Son reciclables al final de su vida útil?

DOCUMENTOS ANALIZADOS:
${uploadedFiles.map((f, i) => `- ${f.name}`).join('\n')}

Responde ÚNICAMENTE con este JSON (sin bloques de código):
{
  "resumen_general": "Resumen ejecutivo del análisis (2-3 frases)",
  "saludabilidad": {
    "puntuacion": número del 1-10,
    "descripcion": "Evaluación detallada de aspectos de salud",
    "alertas": ["lista de alertas si las hay"],
    "certificaciones": ["certificaciones relacionadas con salud"]
  },
  "sostenibilidad": {
    "puntuacion": número del 1-10,
    "descripcion": "Evaluación de sostenibilidad",
    "certificaciones": ["FSC, PEFC, etc si aplica"],
    "origen": "descripción del origen de materiales"
  },
  "huella_carbono": {
    "puntuacion": número del 1-10 (10=baja huella),
    "co2_total_estimado": "X kgCO2eq/unidad o 'No disponible'",
    "descripcion": "Evaluación del impacto de carbono",
    "fases": {
      "fabricacion": "descripción",
      "transporte": "descripción",
      "uso": "descripción"
    }
  },
  "durabilidad": {
    "puntuacion": número del 1-10,
    "vida_util_años": número o "No especificado",
    "descripcion": "Evaluación de durabilidad y mantenimiento"
  },
  "circularidad": {
    "puntuacion": número del 1-10,
    "contenido_reciclado_pct": número o 0,
    "reciclable": boolean,
    "descripcion": "Evaluación de economía circular"
  },
  "recomendaciones": ["lista de 3-5 recomendaciones específicas"],
  "materiales_identificados": ["lista de materiales principales encontrados"]
}`
      })

      // 3. Llamar a Supabase Edge Function
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
      const functionUrl = `${supabaseUrl}/functions/v1/analyze-breeam`

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: userContent }],
          model: 'claude-sonnet-4-5-20250929',
          max_tokens: 4096
        })
      })

      const apiData = await response.json()

      // 4. Parsear respuesta
      let resultado
      try {
        const rawText = apiData.content?.map(c => c.text || '').join('') || ''
        const cleaned = rawText.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim()
        resultado = JSON.parse(cleaned)
      } catch {
        resultado = {
          resumen_general: 'Error al procesar las fichas técnicas. Verifica que los archivos son PDFs o imágenes legibles.',
          saludabilidad: { puntuacion: 0, descripcion: 'No se pudo analizar', alertas: [], certificaciones: [] },
          sostenibilidad: { puntuacion: 0, descripcion: 'No se pudo analizar', certificaciones: [], origen: '' },
          huella_carbono: { puntuacion: 0, co2_total_estimado: 'No disponible', descripcion: 'No se pudo analizar', fases: {} },
          durabilidad: { puntuacion: 0, vida_util_años: 'No especificado', descripcion: 'No se pudo analizar' },
          circularidad: { puntuacion: 0, contenido_reciclado_pct: 0, reciclable: false, descripcion: 'No se pudo analizar' },
          recomendaciones: ['Verifica que los documentos son fichas técnicas legibles'],
          materiales_identificados: []
        }
      }

      setAnalysisResult(resultado)

    } catch (err) {
      console.error('Error:', err)
      alert('Error al analizar materiales. Verifica la consola (F12) para más detalles.')
    }
    setAnalyzing(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Intro */}
      <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 1.75rem', color: '#F5F0EA', fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.625rem', letterSpacing: '-0.01em' }}>
          <Factory size={20} style={{ color: '#F5F0EA' }} /> Análisis de Materiales con IA
        </div>
        <p style={{ margin: 0, color: '#B0A89E', lineHeight: '1.6', fontSize: '0.825rem' }}>
          Sube las fichas técnicas de los materiales de tu proyecto y obtén un análisis completo de saludabilidad, sostenibilidad, huella de carbono y circularidad.
        </p>
      </div>

      {/* Upload zone */}
      <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: '1px solid #DDD5C8', fontFamily: "'DM Sans', sans-serif" }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '0.9rem', fontWeight: '600', color: '#2C2017' }}>Subir Fichas Técnicas</h3>

        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${isDragging ? '#8D9A2E' : '#DDD5C8'}`,
            borderRadius: '6px', padding: '2.5rem 2rem', textAlign: 'center',
            cursor: 'pointer', transition: 'all 0.2s',
            background: isDragging ? 'rgba(141,154,46,0.05)' : '#FAF7F3',
            transform: isDragging ? 'scale(1.01)' : 'scale(1)'
          }}
        >
          <input ref={fileInputRef} type="file" multiple
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleInput} style={{ display: 'none' }} />
          <div style={{ marginBottom: '0.625rem', color: isDragging ? '#8D9A2E' : '#B0A89E' }}>
            <FileText size={28} />
          </div>
          <div style={{ color: isDragging ? '#4A5A10' : '#7A6A5A', fontWeight: '600', fontSize: '0.875rem', marginBottom: '0.3rem' }}>
            {isDragging ? 'Suelta las fichas aquí' : 'Arrastra fichas técnicas aquí'}
          </div>
          <div style={{ color: '#B0A89E', fontSize: '0.75rem' }}>
            PDF o imágenes (JPG, PNG) de fichas técnicas, DAPs, certificaciones
          </div>
        </div>

        {uploadedFiles.length > 0 && (
          <div style={{ marginTop: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {uploadedFiles.map(f => (
              <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.875rem', background: '#FAF7F3', borderRadius: '4px', border: '1px solid #DDD5C8' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FileText size={13} style={{ color: '#7A6A5A' }} />
                  <span style={{ fontSize: '0.8rem', fontWeight: '500', color: '#2C2017' }}>{f.name}</span>
                  <span style={{ fontSize: '0.7rem', color: '#B0A89E' }}>({(f.size / 1024).toFixed(0)} KB)</span>
                </div>
                <button onClick={() => handleRemoveFile(f.id)} style={{ background: 'none', border: 'none', color: '#B0A89E', cursor: 'pointer', padding: '2px' }}>
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={analyzeMaterials}
          disabled={analyzing || !uploadedFiles.length}
          style={{
            width: '100%', padding: '0.875rem', fontSize: '0.875rem', fontWeight: '600',
            borderRadius: '4px', border: 'none', marginTop: '1rem',
            cursor: analyzing || !uploadedFiles.length ? 'not-allowed' : 'pointer',
            background: analyzing || !uploadedFiles.length ? '#EDE7DE' : '#3B2F2F',
            color: analyzing || !uploadedFiles.length ? '#B0A89E' : '#F5F0EA',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.625rem',
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          {analyzing ? (
            <>
              <div style={{ width: '15px', height: '15px', border: '2px solid rgba(245,240,234,0.3)', borderTop: '2px solid #F5F0EA', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              Analizando materiales con IA...
            </>
          ) : (
            <><Factory size={15} /> Analizar Materiales con IA</>
          )}
        </button>
      </div>

      {/* Resultado del análisis */}
      {analysisResult && <MaterialsResults result={analysisResult} />}
    </div>
  )
}

// Componente para mostrar resultados
function MaterialsResults({ result }) {
  const ScoreCard = ({ title, icon: Icon, score, description, color }) => (
    <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: '1px solid #DDD5C8', borderLeft: `3px solid ${color}`, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.875rem' }}>
        <Icon size={18} style={{ color: '#7A6A5A' }} />
        <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#2C2017' }}>{title}</h4>
      </div>
      <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#2C2017', marginBottom: '0.375rem', fontFamily: "'IBM Plex Mono', monospace" }}>
        {score}<span style={{ fontSize: '1rem', color: '#B0A89E', fontWeight: '400' }}>/10</span>
      </div>
      <div style={{ height: '4px', background: '#EDE7DE', borderRadius: '2px', overflow: 'hidden', marginBottom: '0.5rem' }}>
        <div style={{ background: color, height: '100%', width: `${score * 10}%`, borderRadius: '2px' }} />
      </div>
      <p style={{ margin: 0, fontSize: '0.775rem', color: '#7A6A5A', lineHeight: '1.6' }}>{description}</p>
    </div>
  )

  return (
    <>
      {/* Resumen */}
      <div style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderLeft: '3px solid #8D9A2E', borderRadius: '4px', padding: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>
        <h3 style={{ margin: '0 0 0.625rem', fontSize: '0.875rem', fontWeight: '600', color: '#2C2017' }}>Resumen del Análisis</h3>
        <p style={{ margin: 0, fontSize: '0.825rem', color: '#7A6A5A', lineHeight: '1.7' }}>{result.resumen_general}</p>
      </div>

      {/* Puntuaciones */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0.875rem' }}>
        <ScoreCard title="Saludabilidad" icon={Leaf} score={result.saludabilidad.puntuacion} description={result.saludabilidad.descripcion} color="#8D9A2E" />
        <ScoreCard title="Sostenibilidad" icon={ArrowCounterClockwise} score={result.sostenibilidad.puntuacion} description={result.sostenibilidad.descripcion} color="#7A8FA0" />
        <ScoreCard title="Huella de Carbono" icon={Factory} score={result.huella_carbono.puntuacion} description={result.huella_carbono.descripcion} color="#4A4A4A" />
        <ScoreCard title="Durabilidad" icon={CheckCircle} score={result.durabilidad.puntuacion} description={result.durabilidad.descripcion} color="#C98A2E" />
        <ScoreCard title="Circularidad" icon={Drop} score={result.circularidad.puntuacion} description={result.circularidad.descripcion} color="#D4614A" />
      </div>

      {/* Recomendaciones */}
      {result.recomendaciones && result.recomendaciones.length > 0 && (
        <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: '1px solid #DDD5C8', fontFamily: "'DM Sans', sans-serif" }}>
          <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.875rem', fontWeight: '600', color: '#2C2017', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <Warning size={16} style={{ color: '#C98A2E' }} /> Recomendaciones
          </h3>
          {result.recomendaciones.map((rec, i) => (
            <div key={i} style={{ padding: '0.625rem 0.875rem', marginBottom: '0.4rem', background: '#FDF9F2', borderRadius: '4px', borderLeft: '3px solid #C98A2E' }}>
              <div style={{ fontSize: '0.8rem', color: '#5A3D10', lineHeight: '1.55' }}>{rec}</div>
            </div>
          ))}
        </div>
      )}

      {/* Materiales identificados */}
      {result.materiales_identificados && result.materiales_identificados.length > 0 && (
        <div style={{ background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: '1px solid #DDD5C8', fontFamily: "'DM Sans', sans-serif" }}>
          <h3 style={{ margin: '0 0 0.875rem', fontSize: '0.875rem', fontWeight: '600', color: '#2C2017' }}>Materiales Identificados</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {result.materiales_identificados.map((mat, i) => (
              <span key={i} style={{ padding: '0.3rem 0.7rem', background: '#EDE7DE', borderRadius: '3px', fontSize: '0.775rem', color: '#5C4A3A', fontWeight: '500' }}>
                {mat}
              </span>
            ))}
          </div>
        </div>
      )}
    </>
  )
}
